import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-multi-select',
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'relative inline-block' },
  template: `
    <!-- Trigger button -->
    <button
      type="button"
      class="inline-flex items-center gap-2.5 h-8 pl-4 pr-3 rounded-full border text-xs
             bg-white transition-colors
             focus:outline-none focus:ring-2 focus:ring-[#4d806f]"
      [class.border-[#4d806f]]="selected().length > 0 || open()"
      [class.bg-[#f0f7f4]]="selected().length > 0"
      [class.border-[#d1cec7]]="selected().length === 0 && !open()"
      (click)="toggleOpen()"
      [attr.aria-haspopup]="'listbox'"
      [attr.aria-expanded]="open()"
      [attr.aria-label]="label() + ' filter'"
    >
      @if (label()) {
        <span class="text-[#73716d] font-normal flex-shrink-0">{{ label() }}</span>
        <span class="w-px h-3 bg-[#d1cec7] flex-shrink-0"></span>
      }
      <span class="font-semibold text-[#26332b] truncate max-w-[140px]">
        @if (selected().length === 0) {
          {{ placeholder() }}
        } @else if (selected().length === 1) {
          {{ selected()[0] }}
        } @else {
          {{ selected().length }} selected
        }
      </span>
      <svg class="flex-shrink-0 w-3.5 h-3.5 text-[#73716d]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
      </svg>
    </button>

    <!-- Dropdown panel -->
    @if (open()) {
      <div
        class="absolute z-50 mt-1 w-64 rounded-lg border border-[#e2e0d9] bg-white shadow-lg"
        role="listbox"
        [attr.aria-multiselectable]="true"
        [attr.aria-label]="placeholder() + ' options'"
      >
        <!-- Search -->
        <div class="p-2 border-b border-[#e2e0d9]">
          <input
            type="text"
            class="w-full px-2.5 py-1.5 rounded-md border border-[#e2e0d9] text-xs
                   focus:outline-none focus:ring-2 focus:ring-[#4d806f]"
            [placeholder]="'Search ' + placeholder().toLowerCase() + '...'"
            [ngModel]="searchQuery()"
            (ngModelChange)="searchQuery.set($event)"
            (click)="$event.stopPropagation()"
          />
        </div>

        <!-- Options list -->
        <ul class="max-h-56 overflow-y-auto py-1" role="presentation">
          @if (filteredOptions().length === 0) {
            <li class="px-3 py-2 text-xs text-[#73716d] italic">No matches</li>
          }
          @for (opt of filteredOptions(); track opt) {
            <li
              role="option"
              [attr.aria-selected]="selected().includes(opt)"
              class="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer select-none
                     hover:bg-[#f5f3ee] transition-colors"
              (click)="toggle(opt)"
            >
              <span
                class="flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors"
                [class.bg-[#4d806f]]="selected().includes(opt)"
                [class.border-[#4d806f]]="selected().includes(opt)"
                [class.border-[#c8c5be]]="!selected().includes(opt)"
                aria-hidden="true"
              >
                @if (selected().includes(opt)) {
                  <svg class="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="2,6 5,9 10,3" />
                  </svg>
                }
              </span>
              <span class="truncate text-[#26332b]">{{ opt }}</span>
            </li>
          }
        </ul>

        <!-- Footer: clear selection -->
        @if (selected().length > 0) {
          <div class="border-t border-[#e2e0d9] px-3 py-2">
            <button
              type="button"
              class="text-xs text-[#73716d] hover:text-[#26332b] transition-colors cursor-pointer"
              (click)="clearSelection()"
            >
              Clear selection
            </button>
          </div>
        }
      </div>
    }
  `,
})
export class MultiSelectComponent {
  readonly label = input('');
  readonly placeholder = input('All');
  readonly options = input<string[]>([]);
  readonly selected = model<string[]>([]);
  readonly selectionChange = output<string[]>();

  protected readonly open = signal(false);
  protected readonly searchQuery = signal('');

  private readonly host = inject(ElementRef) as ElementRef<HTMLElement>;

  protected readonly filteredOptions = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    return q ? this.options().filter(o => o.toLowerCase().includes(q)) : this.options();
  });

  protected toggleOpen(): void {
    this.open.update(v => !v);
    if (!this.open()) this.searchQuery.set('');
  }

  protected toggle(value: string): void {
    const next = this.selected().includes(value)
      ? this.selected().filter(v => v !== value)
      : [...this.selected(), value];
    this.selected.set(next);
    this.selectionChange.emit(next);
  }

  protected clearSelection(): void {
    this.selected.set([]);
    this.selectionChange.emit([]);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      if (this.open()) {
        this.open.set(false);
        this.searchQuery.set('');
      }
    }
  }
}
